import { Link } from '@tanstack/react-router'
import { ArrowLeft, Calendar, Clock } from 'lucide-react'
import { useProjects } from '@/hooks/useProjects'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useLocalizedPath } from '@/hooks/useLocalizedPath'
import { ProjectCard } from '@/components/ProjectCard'
import { PageLayout } from '@/components/PageLayout'
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData'

type StageId = 'spy' | 'launch' | 'ship' | 'grow'
type CategoryId = 'spy100' | 'launch100' | 'ship100' | 'grow100'

const LANES: { categoryId: CategoryId; stage: StageId }[] = [
  { categoryId: 'spy100', stage: 'spy' },
  { categoryId: 'launch100', stage: 'launch' },
  { categoryId: 'ship100', stage: 'ship' },
  { categoryId: 'grow100', stage: 'grow' },
]

const STAGE_ORDER: StageId[] = ['spy', 'launch', 'ship', 'grow']

function StageProgress({ activeStage }: { activeStage: StageId }) {
  const activeIndex = STAGE_ORDER.indexOf(activeStage)

  return (
    <div className="flex items-center gap-1 mt-2" aria-hidden="true">
      {STAGE_ORDER.map((stage, index) => (
        <div key={stage} className="flex items-center gap-1">
          <span
            className={
              'w-2.5 h-2.5 rounded-full ' +
              (index <= activeIndex ? 'bg-accent-emerald' : 'bg-muted')
            }
          />
          {index < STAGE_ORDER.length - 1 && (
            <span className="w-5 h-px bg-border" />
          )}
        </div>
      ))}
    </div>
  )
}

// 时间线项目组件
function TimelineProject({ project, index }: { project: any; index: number }) {
  const { t, i18n } = useTranslation()
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return t.collections.challenge100.journey.noDate
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    
    return date.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const statusStyles = {
    completed: {
      bg: 'bg-accent-emerald/10',
      text: 'text-accent-emerald',
      border: 'border-accent-emerald',
      label: t.status.completed
    },
    'in-progress': {
      bg: 'bg-accent-blue/10',
      text: 'text-accent-blue',
      border: 'border-accent-blue',
      label: t.status.inProgress
    },
    planned: {
      bg: 'bg-accent-purple/10',
      text: 'text-accent-purple',
      border: 'border-accent-purple',
      label: t.status.planned
    }
  }

  const status = statusStyles[project.status]
  const categoryColors = {
    spy100: 'bg-orange-500/10 text-orange-600',
    launch100: 'bg-blue-500/10 text-blue-600',
    ship100: 'bg-green-500/10 text-green-600',
    grow100: 'bg-purple-500/10 text-purple-600'
  }

  return (
    <div className="relative flex items-center gap-6">
      {/* 时间线 */}
      <div className="flex flex-col items-center">
        <div className={`w-4 h-4 rounded-full border-2 ${status.border} ${status.bg} flex-shrink-0`} />
        <div className="w-px h-12 bg-border mt-2" />
      </div>

      {/* 项目卡片 */}
      <div className="flex-1">
        <div className="bg-card border border-border rounded-xl p-4 hover:shadow-lg hover:border-primary/20 transition-all duration-300 group">
          <div className="flex items-start gap-3">
            {project.image && (
              <img 
                src={project.image} 
                alt={project.title}
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0 group-hover:scale-105 transition-transform duration-300"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                    {project.title}
                  </h3>
                  {project.subtitle && (
                    <p className="text-sm text-muted-foreground truncate">
                      {project.subtitle}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                    {status.label}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[project.category_id as keyof typeof categoryColors] || 'bg-muted text-muted-foreground'}`}>
                    {t.categories[project.category_id as keyof typeof t.categories]}
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {project.description}
              </p>
              
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(project.start_date || project.created_at)}
                </span>
                {project.end_date && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(project.end_date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Challenge100() {
  const { data: projects, isLoading } = useProjects()
  const { t, i18n } = useTranslation()
  const { getLocalizedPath } = useLocalizedPath()

  const lanesWithData = LANES.map((lane) => ({
    ...lane,
    title: t.categories[lane.categoryId],
    projects: (projects || []).filter(
      (p) => p.category_id === lane.categoryId
    ),
  }))

  const totalCount = lanesWithData.reduce(
    (sum, lane) => sum + lane.projects.length,
    0
  )

  // 按时间排序所有项目（用于时间线显示）
  const sortedProjects = (projects || [])
    .filter(p => ['spy100', 'launch100', 'ship100', 'grow100'].includes(p.category_id))
    .sort((a, b) => {
      const dateA = new Date(a.start_date || a.created_at)
      const dateB = new Date(b.start_date || b.created_at)
      return dateB.getTime() - dateA.getTime() // 最新的在前
    })

  const lang = i18n.language as 'en' | 'zh';
  const breadcrumbs = generateBreadcrumbs([
    { label: 'Challenge 100', path: '/challenge100' }
  ], lang);

  return (
    <PageLayout>
      <StructuredData type="website" breadcrumbs={breadcrumbs} />
      <div className="py-20">
        {/* Hero / Intro */}
        <div className="bg-gradient-to-b from-card to-background pb-12">
          <div className="container mx-auto px-6 sm:px-8 lg:px-12">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4">
                {t.collections.challenge100.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-4">
                {t.collections.challenge100.subtitle}
              </p>
              <p className="text-muted-foreground">
                {t.collections.challenge100.intro}
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                {t.collections.challenge100.metrics.totalLabel}：
                {totalCount} · {t.collections.challenge100.metrics.ofHundred}
              </p>
            </div>

            {/* Stage overview cards */}
            <div className="grid md:grid-cols-4 gap-4 mt-10">
              {LANES.map((lane) => {
                const stageInfo = t.collections.challenge100.stages[lane.stage]
                return (
                  <div
                    key={lane.stage}
                    className="bg-card border border-border rounded-2xl p-4 h-full flex flex-col"
                  >
                    <div className="text-xs font-semibold text-muted-foreground mb-2">
                      {t.categories[lane.categoryId]}
                    </div>
                    <h2 className="text-sm font-bold mb-1">
                      {stageInfo.title}
                    </h2>
                    <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                      {stageInfo.description}
                    </p>
                    <StageProgress activeStage={lane.stage} />
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Lanes with projects */}
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 py-12">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-10">
              {t.common.loading}
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {lanesWithData.map((lane) => (
                  <div key={lane.categoryId}>
                    <div className="flex items-baseline justify-between mb-2">
                      <h3 className="text-lg font-bold">{lane.title}</h3>
                      <span className="text-xs text-muted-foreground">
                        {lane.projects.length}
                      </span>
                    </div>
                    <StageProgress activeStage={lane.stage} />
                    <div className="mt-4 space-y-4">
                      {lane.projects.map((project) => (
                        <ProjectCard key={project.id} project={project} />
                      ))}
                      {lane.projects.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          （{t.common.noProjects}）
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Narrative section: deeper explanation of each chapter */}
              <div className="mt-16 border-t border-border pt-12">
                <div className="max-w-3xl mb-10">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                    {t.collections.challenge100.narrative.intro.title}
                  </h2>
                  <p className="text-muted-foreground whitespace-pre-line text-sm sm:text-base">
                    {t.collections.challenge100.narrative.intro.body}
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                  {(['spy', 'ship', 'grow'] as const).map((key) => {
                    const item = t.collections.challenge100.narrative[key]
                    return (
                      <div key={key} className="space-y-3">
                        <h3 className="text-lg font-semibold">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">
                          {item.body}
                        </p>
                        {'caption' in item && item.caption && (
                          <p className="text-xs text-muted-foreground/80 italic">
                            {item.caption}
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* 时间线章节 */}
              <div className="mt-16 border-t border-border pt-12">
                <div className="max-w-3xl mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                    {t.collections.challenge100.journey.allProjects}
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    {t.collections.challenge100.journey.allProjectsSubtitle}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{sortedProjects.length} {t.collections.challenge100.journey.projectCount}</span>
                    <span>•</span>
                    <span>{t.collections.challenge100.journey.scrollHint}</span>
                  </div>
                </div>

                {/* 时间线容器 - 支持滚动 */}
                <div className="relative">
                  <div className="max-h-[500px] overflow-y-auto pr-2 space-y-6 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                    {sortedProjects.length > 0 ? (
                      <>
                        {sortedProjects.map((project, index) => (
                          <TimelineProject 
                            key={project.id} 
                            project={project} 
                            index={index}
                          />
                        ))}
                        {/* 时间线结束标记 */}
                        <div className="flex items-center gap-6">
                          <div className="flex flex-col items-center">
                            <div className="w-2 h-2 rounded-full bg-muted" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-muted-foreground italic">
                              {t.common?.moreProjectsComing || (i18n.language === 'zh' ? '更多项目正在路上...' : 'More projects coming soon...')}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        {t.common.noProjects}
                      </div>
                    )}
                  </div>
                  
                  {/* 滚动渐变效果 */}
                  {sortedProjects.length > 3 && (
                    <>
                      <div className="absolute top-0 left-0 right-2 h-6 bg-gradient-to-b from-background to-transparent pointer-events-none z-10" />
                      <div className="absolute bottom-0 left-0 right-2 h-6 bg-gradient-to-t from-background to-transparent pointer-events-none z-10" />
                    </>
                  )}
                </div>

                {/* 查看完整时间线的链接 */}
                <div className="mt-8 text-center">
                  <Link
                    to={getLocalizedPath('/challenge100/journey')}
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
                  >
                    {t.collections.challenge100.cta.secondary}
                    <ArrowLeft className="w-4 h-4 rotate-180" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  )
}
